import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Badge,
    Button,
    Card,
    CardBody,
    Col,
    Container,
    Form,
    FormGroup,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Row,
    Spinner,
    Table,
} from "reactstrap";
import { FaBuilding, FaEdit, FaPlus, FaTrash } from "react-icons/fa";
import Swal from "sweetalert2";
import config from "../../helpers/config";
import { companyActions } from "../../sagas/companySlice";

const MOROCCAN_BANKS = [
    "Al Barid Bank",
    "Attijariwafa Bank",
    "Bank Al-Maghrib",
    "Bank Assafa",
    "Bank of Africa",
    "Banque Centrale Populaire",
    "BMCI",
    "CFG Bank",
    "CIH Bank",
    "Credit Agricole du Maroc",
    "Credit du Maroc",
    "Societe Generale Maroc",
    "Umnia Bank",
];

const EMPTY_COMPANY = {
    id: null,
    name: "",
    subtitle: "",
    companyType: "",
    address: "",
    phone1: "",
    phone2: "",
    email: "",
    siteWeb: "",
    identificationFiscale: "",
    ice: "",
    patente: "",
    rc: "",
    cnss: "",
    contactName: "",
    contactFonction: "",
    phone1: "",
    contactEmail: "",
    bankName: "",
    rib: "",
    logo: "",
    isDefault: false,
};

const CopmaniesForm = () => {
    const dispatch = useDispatch();
    const { companies, loading } = useSelector((state) => state.companies);

    const [modalOpen, setModalOpen] = useState(false);
    const [formData, setFormData] = useState(EMPTY_COMPANY);
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        dispatch(companyActions.getAll({}));
    }, [dispatch]);

    const sortedCompanies = useMemo(() => {
        return [...(companies || [])].sort((a, b) => {
            if (Boolean(b?.isDefault) !== Boolean(a?.isDefault)) {
                return Number(Boolean(b?.isDefault)) - Number(Boolean(a?.isDefault));
            }
            return (a?.name || "").localeCompare(b?.name || "");
        });
    }, [companies]);

    const bankOptions = useMemo(() => {
        const currentBank = formData.bankName?.trim();

        if (currentBank && !MOROCCAN_BANKS.includes(currentBank)) {
            return [currentBank, ...MOROCCAN_BANKS];
        }

        return MOROCCAN_BANKS;
    }, [formData.bankName]);

    const resetForm = () => {
        setFormData(EMPTY_COMPANY);
        setLogoFile(null);
        setLogoPreview(null);
        setSubmitted(false);
    };

    const toggleModal = () => {
        if (modalOpen) {
            resetForm();
        }
        setModalOpen((prev) => !prev);
    };

    const openCreateModal = () => {
        resetForm();
        setModalOpen(true);
    };

    const openEditModal = (company) => {
        setFormData({ ...EMPTY_COMPANY, ...company });
        setLogoFile(null);
        setLogoPreview(company?.logo || null);
        setSubmitted(false);
        setModalOpen(true);
    };

    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const handleLogoChange = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setLogoFile(file);
        const reader = new FileReader();
        reader.onloadend = () => setLogoPreview(reader.result);
        reader.readAsDataURL(file);
    };

    const getImageSrc = (logo) => {
        if (!logo) return null;
        return logo.startsWith("/uploads") ? `${config.baseURL}${logo}` : logo;
    };

    const refreshCompanies = () => {
        dispatch(companyActions.getAll({}));
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        setSubmitted(true);

        if (!formData.name?.trim()) {
            return;
        }

        const payload = {
            data: {
                company: formData,
                logo: logoFile,
            },
            onSuccess: () => {
                Swal.fire({
                    icon: "success",
                    title: formData.id ? "Entreprise mise a jour" : "Entreprise creee",
                    showConfirmButton: false,
                    timer: 1500,
                });
                toggleModal();
                refreshCompanies();
            },
            onError: (error) => {
                Swal.fire({
                    icon: "error",
                    title: "Operation echouee",
                    text: error?.response?.data?.message || error?.message || "Une erreur est survenue",
                });
            },
        };

        if (formData.id) {
            dispatch(companyActions.update(payload));
            return;
        }

        dispatch(companyActions.create(payload));
    };

    const handleDelete = (company) => {
        Swal.fire({
            title: `Supprimer ${company.name} ?`,
            text: "Cette action est irreversible.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Supprimer",
            cancelButtonText: "Annuler",
        }).then((result) => {
            if (!result.isConfirmed) {
                return;
            }

            dispatch(companyActions.delete({
                id: company.id,
                onSuccess: () => {
                    Swal.fire({
                        icon: "success",
                        title: "Entreprise supprimee",
                        showConfirmButton: false,
                        timer: 1200,
                    });
                    refreshCompanies();
                },
                onError: (error) => {
                    Swal.fire({
                        icon: "error",
                        title: "Suppression echouee",
                        text: error?.response?.data?.message || error?.message || "Une erreur est survenue",
                    });
                },
            }));
        });
    };

    return (
        <Container fluid>
            <Row className="mb-3">
                <Col>
                    <Card className="shadow-sm border-0">
                        <CardBody className="d-flex justify-content-between align-items-center">
                            <h4 className="mb-0 text-primary">
                                <FaBuilding className="me-2" />
                                Gestion des entreprises
                            </h4>
                            <Button color="primary" onClick={openCreateModal}>
                                <FaPlus className="me-1" />
                                Nouvelle entreprise
                            </Button>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <Card className="shadow-sm border-0">
                <CardBody>
                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner />
                        </div>
                    ) : (
                        <Table responsive hover className="align-middle mb-0">
                            <thead>
                                <tr>
                                    <th>Logo</th>
                                    <th>Nom</th>
                                    <th>Defaut</th>
                                    <th>Activite</th>
                                    <th>Telephone</th>
                                    <th>Email</th>
                                    <th>Adresse</th>
                                    <th className="text-end">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedCompanies.map((company) => (
                                    <tr key={company.id}>
                                        <td>
                                            {company.logo ? (
                                                <img
                                                    src={getImageSrc(company.logo)}
                                                    alt={company.name}
                                                    style={{ width: 48, height: 48, objectFit: "cover" }}
                                                    className="rounded border"
                                                />
                                            ) : (
                                                <div
                                                    className="rounded border d-flex align-items-center justify-content-center text-muted"
                                                    style={{ width: 48, height: 48 }}
                                                >
                                                    <FaBuilding />
                                                </div>
                                            )}
                                        </td>
                                        <td>{company.name || "-"}</td>
                                        <td>
                                            {company.isDefault ? (
                                                <Badge color="success">Par defaut</Badge>
                                            ) : (
                                                <span className="text-muted">-</span>
                                            )}
                                        </td>
                                        <td>{company.subtitle || company.companyType || "-"}</td>
                                        <td>{company.phone1 || company.phone || "-"}</td>
                                        <td>{company.email || "-"}</td>
                                        <td>{company.address || "-"}</td>
                                        <td className="text-end">
                                            <div className="d-flex justify-content-end gap-2">
                                                <Button color="warning" size="sm" onClick={() => openEditModal(company)}>
                                                    <FaEdit />
                                                </Button>
                                                <Button color="danger" size="sm" onClick={() => handleDelete(company)}>
                                                    <FaTrash />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {sortedCompanies.length === 0 && (
                                    <tr>
                                        <td colSpan="8" className="text-center py-5 text-muted">
                                            Aucune entreprise trouvee.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    )}
                </CardBody>
            </Card>

            <Modal isOpen={modalOpen} toggle={toggleModal} size="xl">
                <Form onSubmit={handleSubmit}>
                    <ModalHeader toggle={toggleModal}>
                        {formData.id ? "Modifier l'entreprise" : "Nouvelle entreprise"}
                    </ModalHeader>
                    <ModalBody style={{ maxHeight: "70vh", overflowY: "auto" }}>
                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Nom</Label>
                                    <Input
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        invalid={submitted && !formData.name?.trim()}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Activite</Label>
                                    <Input name="subtitle" value={formData.subtitle} onChange={handleChange} />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup check className="mt-md-4 pt-md-2">
                                    <Input
                                        id="isDefault"
                                        name="isDefault"
                                        type="checkbox"
                                        checked={Boolean(formData.isDefault)}
                                        onChange={handleChange}
                                    />
                                    <Label check htmlFor="isDefault">
                                        Definir comme entreprise par defaut
                                    </Label>
                                </FormGroup>
                            </Col>
                            
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Email</Label>
                                    <Input name="email" type="email" value={formData.email} onChange={handleChange} />
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Telephone 1</Label>
                                    <Input name="phone1" value={formData.phone1} onChange={handleChange} />
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Telephone 2</Label>
                                    <Input name="phone2" value={formData.phone2} onChange={handleChange} />
                                </FormGroup>
                            </Col>
                            <Col md={12}>
                                <FormGroup>
                                    <Label>Adresse</Label>
                                    <Input type="textarea" rows={3} name="address" value={formData.address} onChange={handleChange} />
                                </FormGroup>
                            </Col>
                            
                            <Col md={3}>
                                <FormGroup>
                                    <Label>Identification fiscale</Label>
                                    <Input
                                        name="identificationFiscale"
                                        value={formData.identificationFiscale}
                                        onChange={handleChange}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={3}>
                                <FormGroup>
                                    <Label>ICE</Label>
                                    <Input name="ice" value={formData.ice} onChange={handleChange} />
                                </FormGroup>
                            </Col>
                            <Col md={3}>
                                <FormGroup>
                                    <Label>Patente</Label>
                                    <Input name="patente" value={formData.patente} onChange={handleChange} />
                                </FormGroup>
                            </Col>
                            <Col md={3}>
                                <FormGroup>
                                    <Label>RC</Label>
                                    <Input name="rc" value={formData.rc} onChange={handleChange} />
                                </FormGroup>
                            </Col>
                            <Col md={3}>
                                <FormGroup>
                                    <Label>CNSS</Label>
                                    <Input name="cnss" value={formData.cnss} onChange={handleChange} />
                                </FormGroup>
                            </Col>
                            <Col md={3}>
                                <FormGroup>
                                    <Label>Banque</Label>
                                    <Input type="select" name="bankName" value={formData.bankName} onChange={handleChange}>
                                        <option value="">Selectionner une banque</option>
                                        {bankOptions.map((bank) => (
                                            <option key={bank} value={bank}>
                                                {bank}
                                            </option>
                                        ))}
                                    </Input>
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>RIB</Label>
                                    <Input name="rib" value={formData.rib} onChange={handleChange} />
                                </FormGroup>
                            </Col>
                            
                        </Row>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="primary" type="submit" disabled={loading}>
                            {formData.id ? "Enregistrer" : "Creer"}
                        </Button>
                        <Button color="secondary" onClick={toggleModal}>
                            Annuler
                        </Button>
                    </ModalFooter>
                </Form>
            </Modal>
        </Container>
    );
};

export default CopmaniesForm;
